import { useEffect, useState } from "react";
import { Hammer, Plus, X, Loader2, Clock, CheckCircle2, Wrench, CalendarClock, Mail, User as UserIcon, AlertTriangle } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle, EmptyState, Pill } from "@/components/Brutal";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VoiceButton, MultiPhotoInput } from "@/components/AiComponents";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useModuleGuard } from "@/lib/useModuleGuard";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const isFacilitair = (u) => u && (u.role === "super_admin" || u.role === "beheerder" || (u.extra_roles || []).includes("facilitair"));

const PRIORITIES = [
  { value: "spoed", label: "Spoed", cls: "bg-red-500 text-white", dot: "🔴" },
  { value: "urgent", label: "Urgent", cls: "bg-brand-orange text-white", dot: "🟠" },
  { value: "geen_haast", label: "Geen haast", cls: "bg-brand-green text-brand-ink", dot: "🟢" },
];
const prioMeta = (v) => PRIORITIES.find((p) => p.value === v) || PRIORITIES[2];

const STATUS = {
  open: { label: "Open", cls: "bg-brand-yellow", icon: Clock },
  bezig: { label: "In behandeling", cls: "bg-brand-blue text-white", icon: Wrench },
  afgerond: { label: "Afgerond", cls: "bg-brand-green", icon: CheckCircle2 },
};

const backend = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u?.startsWith("http") ? u : `${backend}${u}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "");

export default function Klusjes() {
  useModuleGuard("klusjes");
  const { user } = useAuth();
  const t = useT();
  const fac = isFacilitair(user);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("open");
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/klusjes");
    setItems(data);
    if (detail) setDetail(data.find((k) => k.id === detail.id) || null);
  };
  useEffect(() => { load(); }, []);

  const open = items.filter((k) => k.status !== "afgerond");
  const done = items.filter((k) => k.status === "afgerond");
  const shown = tab === "open" ? open : done;

  return (
    <div className="space-y-4" data-testid="klusjes-page">
      <div className="flex items-center justify-between">
        <SectionTitle sub={fac ? "Meldingen & reparaties beheren" : "Meld een reparatie of storing"}>Klusjes</SectionTitle>
        <BrutalButton data-testid="new-klus-btn" onClick={() => setCreate(true)}><Plus className="w-5 h-5" strokeWidth={3} />{t("Melden")}</BrutalButton>
      </div>

      {fac && <Pill className="bg-brand-violet/20" data-testid="facilitair-badge"><Wrench className="w-3 h-3" strokeWidth={2.5} />Facilitair</Pill>}

      <div className="flex gap-2">
        <button data-testid="klus-tab-open" onClick={() => setTab("open")}
          className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${tab === "open" ? "bg-brand-ink text-white" : "bg-white"}`}>Open ({open.length})</button>
        <button data-testid="klus-tab-done" onClick={() => setTab("done")}
          className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${tab === "done" ? "bg-brand-ink text-white" : "bg-white"}`}>Afgerond ({done.length})</button>
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Hammer} title="Geen klusjes" text={fac ? "Er staan geen meldingen open." : "Meld een kapotte lamp, klemmende deur of andere storing."} />
      ) : (
        <div className="space-y-3">
          {shown.map((k) => {
            const p = prioMeta(k.priority);
            const s = STATUS[k.status] || STATUS.open;
            const SIcon = s.icon;
            return (
              <BrutalCard key={k.id} data-testid={`klus-${k.id}`} onClick={() => setDetail(k)} className="cursor-pointer">
                <div className="flex items-start gap-3">
                  {k.photo_urls?.[0] && <img src={imgSrc(k.photo_urls[0])} alt="" className="w-14 h-14 object-cover brutal-sm rounded-lg shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Pill className={p.cls}>{t(p.label)}</Pill>
                      <Pill className={s.cls}><SIcon className="w-3 h-3" strokeWidth={2.5} />{t(s.label)}</Pill>
                    </div>
                    <h3 className="font-head font-bold text-lg leading-tight truncate">{k.title}</h3>
                    {k.description && <p className="text-sm text-brand-ink/60 line-clamp-2">{k.description}</p>}
                    <div className="flex items-center gap-3 mt-1 text-xs font-semibold text-brand-ink/50">
                      {fac && <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" strokeWidth={2.5} />{k.reporter_name}</span>}
                      {k.expected_date && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" strokeWidth={2.5} />{fmtDate(k.expected_date)}</span>}
                    </div>
                  </div>
                </div>
              </BrutalCard>
            );
          })}
        </div>
      )}

      {create && <CreateKlus onClose={() => setCreate(false)} onDone={load} />}
      {detail && <KlusDetail klus={detail} fac={fac} me={user.user_id} onClose={() => setDetail(null)} onChange={load} />}
    </div>
  );
}

function CreateKlus({ onClose, onDone }) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("geen_haast");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error(t("Geef een korte titel op"));
    setBusy(true);
    try {
      await api.post("/klusjes", { title, description, priority, photo_urls: photos });
      toast.success(t("Klusje gemeld 🔧"));
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Klusje melden")}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Meld een reparatie of storing met foto's en omschrijving.</DialogDescription>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-bold">{t("Wat is er aan de hand?")}</label>
            <input data-testid="klus-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Bijv. Kapotte lamp gang")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-bold">{t("Omschrijving")}</label>
              <VoiceButton onText={(txt) => setDescription((d) => (d ? d + " " : "") + txt)} testid="klus-voice" />
            </div>
            <textarea data-testid="klus-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder={t("Beschrijf het probleem (of spreek in)…")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          </div>
          <div>
            <label className="text-sm font-bold">{t("Hoe dringend?")}</label>
            <div className="flex gap-2 mt-1">
              {PRIORITIES.map((p) => (
                <button key={p.value} type="button" data-testid={`klus-prio-${p.value}`} onClick={() => setPriority(p.value)}
                  className={`flex-1 brutal-sm brutal-press rounded-xl py-2 text-sm font-bold ${priority === p.value ? p.cls : "bg-white"}`}>{p.dot} {t(p.label)}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold">{t("Foto's")}</label>
            <div className="mt-1"><MultiPhotoInput value={photos} onChange={setPhotos} testid="klus-photos" /></div>
          </div>
        </div>
        <BrutalButton data-testid="submit-klus-btn" className="w-full mt-2" disabled={busy} onClick={submit}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" strokeWidth={2.5} />}{t("Melden")}
        </BrutalButton>
      </DialogContent>
    </Dialog>
  );
}

function KlusDetail({ klus, fac, me, onClose, onChange }) {
  const confirm = useConfirm();
  const t = useT();
  const [mode, setMode] = useState(null); // "update" | "close"
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [expected, setExpected] = useState(klus.expected_date || "");
  const [busy, setBusy] = useState(false);
  const p = prioMeta(klus.priority);
  const s = STATUS[klus.status] || STATUS.open;

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChange(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = (status) => act(() => api.put(`/klusjes/${klus.id}/status`, { status }), t("Status bijgewerkt"));
  const saveExpected = () => act(() => api.put(`/klusjes/${klus.id}/expected-date`, { expected_date: expected || null }), t("Datum opgeslagen"));
  const submitUpdate = () => {
    if (!text.trim() && photos.length === 0) return toast.error(t("Voeg tekst of foto toe"));
    act(() => api.post(`/klusjes/${klus.id}/update`, { text, photo_urls: photos }), t("Update geplaatst")).then(() => { setMode(null); setText(""); setPhotos([]); });
  };
  const submitClose = () => act(() => api.post(`/klusjes/${klus.id}/close`, { note: text, photo_urls: photos }), t("Klusje afgerond ✅")).then(() => { setMode(null); setText(""); setPhotos([]); });
  const remove = async () => { if (!(await confirm({ title: t("Klusje verwijderen?") }))) return; act(() => api.delete(`/klusjes/${klus.id}`), t("Verwijderd")).then(onClose); };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto" data-testid="klus-detail">
        <DialogHeader><DialogTitle className="font-head font-black text-xl pr-6">{klus.title}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Details van het klusje.</DialogDescription>

        <div className="flex items-center gap-2 flex-wrap">
          <Pill className={p.cls}>{t(p.label)}</Pill>
          <Pill className={s.cls}>{t(s.label)}</Pill>
          {klus.expected_date && <Pill className="bg-brand-cream"><CalendarClock className="w-3 h-3" strokeWidth={2.5} />{t("Rond")} {fmtDate(klus.expected_date)}</Pill>}
        </div>

        {klus.description && <p className="text-sm font-medium">{klus.description}</p>}

        {klus.photo_urls?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {klus.photo_urls.map((u, i) => <img key={i} src={imgSrc(u)} alt="" className="w-24 h-24 object-cover brutal-sm rounded-lg" data-testid={`klus-photo-${i}`} />)}
          </div>
        )}

        {fac && (
          <div className="brutal-sm rounded-xl bg-brand-cream p-3 text-sm">
            <p className="font-bold flex items-center gap-1.5"><UserIcon className="w-4 h-4" strokeWidth={2.5} />{t("Melder")}</p>
            <p>{klus.reporter_name}</p>
            {klus.reporter_email && <a href={`mailto:${klus.reporter_email}`} data-testid="klus-mailto" className="flex items-center gap-1.5 text-brand-violet font-semibold mt-0.5"><Mail className="w-4 h-4" strokeWidth={2.5} />{klus.reporter_email}</a>}
          </div>
        )}

        {klus.updates?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-bold">{t("Updates")}</p>
            {klus.updates.map((u) => (
              <div key={u.id} className="brutal-sm rounded-xl bg-white p-3" data-testid={`klus-update-${u.id}`}>
                <p className="text-xs font-bold text-brand-ink/50">{u.author_name} · {fmtDate(u.created_at)}</p>
                {u.text && <p className="text-sm">{u.text}</p>}
                {u.photo_urls?.length > 0 && <div className="flex gap-2 mt-1">{u.photo_urls.map((x, i) => <img key={i} src={imgSrc(x)} alt="" className="w-16 h-16 object-cover brutal-sm rounded-lg" />)}</div>}
              </div>
            ))}
          </div>
        )}

        {klus.status === "afgerond" && klus.resolution_note != null && (
          <div className="brutal-sm rounded-xl bg-brand-green/40 p-3">
            <p className="text-sm font-bold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />{t("Opgelost")}</p>
            {klus.resolution_note && <p className="text-sm">{klus.resolution_note}</p>}
            {klus.resolution_photo_urls?.length > 0 && <div className="flex gap-2 mt-1">{klus.resolution_photo_urls.map((x, i) => <img key={i} src={imgSrc(x)} alt="" className="w-16 h-16 object-cover brutal-sm rounded-lg" />)}</div>}
          </div>
        )}

        {/* Facilitair actions */}
        {fac && klus.status !== "afgerond" && !mode && (
          <div className="space-y-2 border-t-2 border-brand-ink/10 pt-3">
            <div className="flex gap-2 flex-wrap">
              {klus.status === "open" && <BrutalButton data-testid="klus-start-btn" variant="blue" size="sm" disabled={busy} onClick={() => setStatus("bezig")}><Wrench className="w-4 h-4" strokeWidth={2.5} />{t("In behandeling")}</BrutalButton>}
              <BrutalButton data-testid="klus-update-btn" variant="white" size="sm" onClick={() => { setMode("update"); setText(""); setPhotos([]); }}>{t("Update geven")}</BrutalButton>
              <BrutalButton data-testid="klus-close-btn" variant="green" size="sm" onClick={() => { setMode("close"); setText(""); setPhotos([]); }}><CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />{t("Afronden")}</BrutalButton>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs font-bold">{t("Verwachte oplosdatum")}</label>
                <DatePicker testid="klus-expected-input" value={expected} onChange={(v) => setExpected(v)} className="px-3 py-2 mt-1" />
              </div>
              <BrutalButton data-testid="klus-expected-save" variant="dark" size="sm" disabled={busy} onClick={saveExpected}>{t("Opslaan")}</BrutalButton>
            </div>
          </div>
        )}

        {fac && mode && (
          <div className="space-y-2 border-t-2 border-brand-ink/10 pt-3">
            <div className="flex items-center justify-between">
              <p className="font-bold text-sm">{mode === "close" ? t("Toelichting bij afronden") : t("Statusupdate")}</p>
              <VoiceButton onText={(txt) => setText((d) => (d ? d + " " : "") + txt)} testid="klus-action-voice" />
            </div>
            <textarea data-testid="klus-action-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={mode === "close" ? t("Wat is er gedaan?") : t("Waar sta je nu?")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            <MultiPhotoInput value={photos} onChange={setPhotos} testid="klus-action-photos" />
            <div className="flex gap-2">
              <BrutalButton data-testid="klus-action-submit" variant={mode === "close" ? "green" : "blue"} className="flex-1" disabled={busy} onClick={mode === "close" ? submitClose : submitUpdate}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{mode === "close" ? t("Afronden") : t("Plaatsen")}
              </BrutalButton>
              <BrutalButton variant="white" onClick={() => setMode(null)}>{t("Annuleer")}</BrutalButton>
            </div>
          </div>
        )}

        {klus.reporter_id === me && (
          <button data-testid="klus-delete-btn" onClick={remove} className="text-xs font-bold text-red-500 flex items-center gap-1 self-start"><X className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Melding verwijderen")}</button>
        )}
      </DialogContent>
    </Dialog>
  );
}
